import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import TopBar from '../layout/TopBar';
import { useHGS } from '../context/HGSContext';
import { computeResidualScore, ragFor, bandFor, FRAUD_TRIGGER_IDS, PCC_MANDATORY_IDS } from '../data/riskUniverse';
import { ArchitectureProgress } from './RiskUniversePage';
import { computeDlcAutofill, autoPopStats } from '../data/dlcAutofill';
import { filterRowsForUser } from '../data/rbac';
import {
  GaugeCircle, ShieldAlert, ShieldCheck, ClipboardCheck, FileSignature, Wrench,
  TrendingUp, Zap, AlertTriangle, ArrowRight, FlameKindling, CheckCircle2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const RAG_COLOR = {
  Red: 'bg-red-500',
  Amber: 'bg-amber-400',
  Yellow: 'bg-yellow-300',
  Green: 'bg-emerald-400',
};

export default function GRCReviewPage() {
  const { riskUniverse, countermeasures, formResponses, masterMeta, masters, user } = useHGS();
  const navigate = useNavigate();

  const risks = useMemo(() => riskUniverse.map((r) => {
    const { inherent, residual } = computeResidualScore(r);
    return { ...r, inherent, residual, rag: ragFor(residual), band: bandFor(residual) };
  }), [riskUniverse]);

  const ragCounts = useMemo(() => {
    const c = { Red: 0, Amber: 0, Yellow: 0, Green: 0 };
    risks.forEach((r) => { c[r.rag] = (c[r.rag] || 0) + 1; });
    return c;
  }, [risks]);

  const highRisks = useMemo(() => risks.filter((r) => r.band === 'High').sort((a, b) => b.residual - a.residual).slice(0, 6), [risks]);

  const fraudRisks = useMemo(() => risks.filter((r) => FRAUD_TRIGGER_IDS.includes(r.id) && (r.band === 'Medium' || r.band === 'High')), [risks]);
  const pccRisks = useMemo(() => risks.filter((r) => PCC_MANDATORY_IDS.includes(r.id)), [risks]);

  // Division compliance score — based on % of responses submitted per module
  const moduleStats = useMemo(() => {
    const compute = (key, rowsArr) => {
      const total = rowsArr.length;
      const submitted = Object.keys(formResponses).filter((k) => k.startsWith(`${key}:`)).length;
      const pct = total ? Math.round((submitted / total) * 100) : 0;
      return { total, submitted, pct };
    };
    return {
      fraud: compute('fraud', masters.fraud || []),
      pcc: compute('pcc', masters.pcc || []),
      dlc: compute('dlc', masters.dlc || []),
    };
  }, [masters, formResponses]);

  const complianceScore = useMemo(() => {
    const vals = [moduleStats.fraud.pct, moduleStats.pcc.pct, moduleStats.dlc.pct];
    return Math.round(vals.reduce((s, v) => s + v, 0) / vals.length);
  }, [moduleStats]);

  // DLC auto-pop rate — real derivation from Fraud + PCC responses by department (dept-scoped for Department Heads)
  const autoPop = useMemo(() => {
    const autofill = computeDlcAutofill({
      dlcRows: filterRowsForUser(user, masters.dlc || []),
      fraudRows: filterRowsForUser(user, masters.fraud || []),
      pccRows: filterRowsForUser(user, masters.pcc || []),
      formResponses,
    });
    return autoPopStats(autofill).rate;
  }, [masters, formResponses, user]);

  const overdueCms = countermeasures.filter((c) => c.status !== 'Closed' && c.daysOverdue > 0);
  const escalatedCms = countermeasures.filter((c) => c.escalated);

  // Compliance gaps: PCC-mandatory risks without submitted responses
  const gaps = useMemo(() => {
    const gapList = [];
    pccRisks.forEach((r) => {
      gapList.push({
        area: `PCC · ${r.id}`,
        description: r.description,
        severity: r.band === 'High' ? 'Red' : 'Amber',
      });
    });
    if (moduleStats.dlc.pct < 50) {
      gapList.push({ area: 'DLC', description: `Only ${moduleStats.dlc.pct}% of declarations complete`, severity: 'Amber' });
    }
    if (moduleStats.fraud.pct < 50 && fraudRisks.length > 0) {
      gapList.push({ area: 'Fraud', description: `Fraud triggered but only ${moduleStats.fraud.pct}% of controls reviewed`, severity: 'Red' });
    }
    return gapList.slice(0, 5);
  }, [pccRisks, fraudRisks, moduleStats]);

  return (
    <div>
      <TopBar
        title="GRC Consolidated Review Dashboard"
        subtitle="Step 5 of 5 · Residual risk, gaps, division score, escalations · final board-level view of HGS posture"
        breadcrumbs={['HGS Architecture', 'GRC Review']}
      />

      <div className="p-8 space-y-5">
        <ArchitectureProgress step={5} />

        {/* Hero KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <HeroKPI
            label="Overall Compliance Score"
            value={`${complianceScore}%`}
            accent="from-[#CC0000] to-[#990000]"
            icon={GaugeCircle}
            sub={`${moduleStats.fraud.submitted + moduleStats.pcc.submitted + moduleStats.dlc.submitted} items actioned`}
            testId="kpi-compliance-score"
          />
          <HeroKPI
            label="DLC Auto-Pop Rate"
            value={`${autoPop}%`}
            accent="from-violet-600 to-[#7C3AED]"
            icon={Zap}
            sub="Target 60–70%"
            trend={autoPop >= 60 && autoPop <= 75 ? '✓ On target' : 'Out of band'}
            trendOk={autoPop >= 60 && autoPop <= 75}
            testId="kpi-autopop-rate"
          />
          <HeroKPI
            label="High-Risk Items"
            value={highRisks.length}
            accent="from-orange-500 to-red-600"
            icon={AlertTriangle}
            sub={`${ragCounts.Red} Red · ${ragCounts.Amber} Amber`}
            onClick={() => navigate('/risk?filter=high-risk')}
            testId="kpi-high-risks"
          />
          <HeroKPI
            label="Overdue Countermeasures"
            value={overdueCms.length}
            accent="from-amber-500 to-orange-600"
            icon={FlameKindling}
            sub={`${escalatedCms.length} escalated to Compliance Officer`}
            onClick={() => navigate('/countermeasures')}
            testId="kpi-overdue-cms"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Residual Risk Breakdown */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-bold text-slate-900">Residual Risk Posture</h3>
                <p className="text-[11px] text-slate-500">45 risks · auto-calculated from Frequency × Impact − Controls</p>
              </div>
              <Link to="/risk" className="text-[11px] font-bold text-[#CC0000] hover:underline inline-flex items-center gap-1">
                View all <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            {/* RAG bar */}
            <div className="flex h-8 rounded-lg overflow-hidden mb-4 border border-slate-200">
              {['Red', 'Amber', 'Yellow', 'Green'].map((k) => {
                const pct = risks.length ? (ragCounts[k] / risks.length) * 100 : 0;
                if (pct === 0) return null;
                return (
                  <div key={k} className={cn('flex items-center justify-center text-[10px] font-bold text-white', RAG_COLOR[k])} style={{ width: `${pct}%` }} title={`${k}: ${ragCounts[k]}`}>
                    {pct > 8 ? `${k} · ${ragCounts[k]}` : ragCounts[k]}
                  </div>
                );
              })}
            </div>

            {/* High risks table */}
            <div className="mt-3">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Top High-Risk items</div>
              <div className="space-y-1.5">
                {highRisks.map((r) => (
                  <div key={r.id} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50 border border-slate-100">
                    <span className="font-mono text-[10px] font-bold text-slate-700 w-10">{r.id}</span>
                    <span className="text-xs text-slate-700 flex-1 truncate">{r.description}</span>
                    <span className="text-[10px] font-bold text-slate-500 tabular-nums">Res {r.residual}</span>
                    <span className={cn('w-2.5 h-2.5 rounded-full', RAG_COLOR[r.rag])} />
                  </div>
                ))}
                {highRisks.length === 0 && <p className="text-xs text-slate-400 italic text-center py-4">No high-band risks currently — adjust controls in Risk Universe to see changes here.</p>}
              </div>
            </div>
          </div>

          {/* Module compliance */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-bold text-slate-900 mb-4">Module Compliance</h3>
            <div className="space-y-3">
              <ModuleBar label="Fraud" pct={moduleStats.fraud.pct} submitted={moduleStats.fraud.submitted} total={moduleStats.fraud.total} icon={ShieldCheck} tone="sky" to="/fraud" />
              <ModuleBar label="PCC" pct={moduleStats.pcc.pct} submitted={moduleStats.pcc.submitted} total={moduleStats.pcc.total} icon={ClipboardCheck} tone="violet" to="/pcc" />
              <ModuleBar label="DLC" pct={moduleStats.dlc.pct} submitted={moduleStats.dlc.submitted} total={moduleStats.dlc.total} icon={FileSignature} tone="amber" to="/dlc" />
            </div>

            <div className="mt-5 pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-[11px] font-bold text-slate-700">Divisions on track</span>
              </div>
              <p className="text-[11px] text-slate-500">Manufacturing · R&D · Procurement · Legal</p>
            </div>
          </div>
        </div>

        {/* Gaps + Escalations */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-bold text-slate-900">Compliance Gaps</h3>
            </div>
            <div className="space-y-2">
              {gaps.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No compliance gaps detected.</p>
              ) : gaps.map((g) => (
                <div key={`gap-${g.area}`} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <div className={cn('w-1.5 h-8 rounded-full flex-shrink-0', g.severity === 'Red' ? 'bg-red-500' : 'bg-amber-400')} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-bold text-slate-700">{g.area}</div>
                    <p className="text-xs text-slate-600">{g.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Wrench className="w-4 h-4 text-red-600" />
                <h3 className="text-sm font-bold text-slate-900">Countermeasure Escalations</h3>
              </div>
              <Link to="/countermeasures" className="text-[11px] font-bold text-[#CC0000] hover:underline inline-flex items-center gap-1">
                View all <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            {countermeasures.length === 0 ? (
              <p className="text-xs text-slate-400 italic">No countermeasures yet. Countermeasures auto-generate from every No/NG answer.</p>
            ) : (
              <div className="space-y-2">
                {countermeasures.slice(0, 5).map((c) => (
                  <div key={c.id} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-white text-slate-700 border border-slate-200">{c.module}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-900 truncate">{c.issue}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        {c.owner} · Due {c.dueDate}
                        {c.daysOverdue > 0 && <span className="text-red-700 font-bold ml-1">· {c.daysOverdue}d overdue</span>}
                      </p>
                    </div>
                    {c.escalated && <FlameKindling className="w-3 h-3 text-red-600 flex-shrink-0" />}
                    {c.status === 'Closed' && <CheckCircle2 className="w-3 h-3 text-emerald-600 flex-shrink-0" />}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroKPI({ label, value, accent, icon: Icon, sub, trend, trendOk, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      data-testid={testId}
      className={cn(
        'group text-left rounded-xl p-5 text-white relative overflow-hidden bg-gradient-to-br shadow-sm',
        accent,
        onClick && 'hover:shadow-md transition cursor-pointer',
        !onClick && 'cursor-default'
      )}
    >
      <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/10 blur-2xl" />
      <div className="relative">
        <div className="flex items-start justify-between mb-3">
          <div className="w-9 h-9 rounded-lg bg-white/15 backdrop-blur flex items-center justify-center">
            <Icon className="w-4 h-4" />
          </div>
          {onClick && <ArrowRight className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition" />}
        </div>
        <div className="text-[10px] font-bold uppercase tracking-wider opacity-80 mb-1">{label}</div>
        <div className="text-3xl font-bold tracking-tight">{value}</div>
        {sub && <div className="text-[11px] opacity-80 mt-1">{sub}</div>}
        {trend && (
          <div className={cn('text-[10px] font-bold mt-1', trendOk ? 'text-emerald-200' : 'text-amber-200')}>
            {trend}
          </div>
        )}
      </div>
    </button>
  );
}

function ModuleBar({ label, pct, submitted, total, icon: Icon, tone, to }) {
  const toneMap = {
    sky: 'text-sky-700 bg-sky-100',
    violet: 'text-violet-700 bg-violet-100',
    amber: 'text-amber-700 bg-amber-100',
  };
  const barMap = {
    sky: 'bg-sky-500',
    violet: 'bg-violet-500',
    amber: 'bg-amber-500',
  };
  return (
    <Link to={to} className="block group">
      <div className="flex items-center gap-2 mb-1">
        <div className={cn('w-6 h-6 rounded-md flex items-center justify-center', toneMap[tone])}>
          <Icon className="w-3 h-3" />
        </div>
        <span className="text-xs font-bold text-slate-700 flex-1">{label}</span>
        <span className="text-[11px] font-bold text-slate-900 tabular-nums">{pct}%</span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={cn('h-full rounded-full transition-all', barMap[tone])} style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[10px] text-slate-500 mt-0.5">{submitted} of {total} submitted</div>
    </Link>
  );
}
